import { CSS } from "../../constants/css";
import Lenis from "lenis";

const ARIA_EXPANDED = "aria-expanded";

const instance = ($el: HTMLElement) => {
  let accordionWatcher: number | null = null;

  const dom = {
    $menu: $el.querySelector<HTMLElement>(".js-menu"),
    $openBtn: $el.querySelector<HTMLElement>(".js-menu-open"),
    $closeBtn: $el.querySelector<HTMLElement>(".js-menu-close"),
    $backBtns: $el.querySelectorAll<HTMLElement>(".js-menu-back"),
    $overlay: $el.querySelector<HTMLElement>(".js-menu-overlay"),
    $navListItems: $el.querySelectorAll<HTMLElement>(".js-nav-list-item"),
    $mainNav: $el.querySelector<HTMLElement>(".js-main-nav"),
    $subNavs: $el.querySelectorAll<HTMLElement>(".js-subnav"),
  };

  const startAccordionWatcher = () => {
    // Removed automatic accordion expansion - accordion items should only
    // open when users explicitly click or use keyboard navigation
    if (accordionWatcher) {
      clearInterval(accordionWatcher);
    }
  };

  const stopAccordionWatcher = () => {
    if (accordionWatcher) {
      clearInterval(accordionWatcher);
      accordionWatcher = null;
    }
  };

  const findSubnavFor = (navItem: HTMLElement) => {
    const subnavId = navItem.getAttribute("subnav-id");

    if (!subnavId) {
      return null;
    }

    return Array.from(dom.$subNavs).find(
      subNav => subNav.getAttribute("subnav-id") === subnavId,
    ) ?? null;
  };

  // Submenu state belongs only on items that reveal a subnav panel
  const setExpandedState = (navItem: HTMLElement, isExpanded: boolean) => {
    if (findSubnavFor(navItem)) {
      navItem.setAttribute(ARIA_EXPANDED, isExpanded.toString());
    }
  };

  const setFocusableElements = (elements: Iterable<HTMLElement>, tabindex: string) => {
    for (const el of elements) {
      if (tabindex === "-1") {
        el.setAttribute("tabindex", tabindex);
      } else {
        el.removeAttribute("tabindex");
      }
    }
  };

  const enableSubnavFocusability = (subNav: HTMLElement) => {
    // Make accordion toggle buttons focusable (these should be the first tab stops)
    const accordionToggleButtons = subNav.querySelectorAll<HTMLElement>(".js-accordion-toggle-btn");
    setFocusableElements(accordionToggleButtons, "0");

    // Make non-accordion focusable elements focusable
    const nonAccordionFocusableElements = subNav.querySelectorAll<HTMLElement>(
      "a:not(.js-accordion-item-content *):not(.js-accordion-toggle-btn), button:not(.js-accordion-item-content *):not(.js-accordion-toggle-btn), input:not(.js-accordion-item-content *), textarea:not(.js-accordion-item-content *), select:not(.js-accordion-item-content *), [tabindex]:not(.js-accordion-item-content *):not(.js-accordion-toggle-btn)",
    );
    setFocusableElements(nonAccordionFocusableElements, "0");

    // Dropdown content is only reachable while its dropdown is open; the
    // accordion component handles it from there
    const isInOpenDropdown = ($el: HTMLElement) => $el.closest(".js-accordion-item")
      ?.querySelector(".js-accordion-toggle-btn")?.getAttribute(ARIA_EXPANDED) === "true";
    const accordionContentElements = Array.from(subNav.querySelectorAll<HTMLElement>(
      ".js-accordion-item-content a, .js-accordion-item-content button, .js-accordion-item-content input, .js-accordion-item-content textarea, .js-accordion-item-content select, .js-accordion-item-content [tabindex], .js-nav-sublist-item",
    ));
    setFocusableElements(accordionContentElements.filter($el => !isInOpenDropdown($el)), "-1");
    setFocusableElements(accordionContentElements.filter(isInOpenDropdown), "0");

    subNav.removeAttribute("aria-hidden");
  };

  const disableSubnavFocusability = (subNav: HTMLElement) => {
    // When making subnav non-focusable, disable all focusable elements
    const allFocusableElements = subNav.querySelectorAll<HTMLElement>(
      "a, button, input, textarea, select, [tabindex]",
    );

    setFocusableElements(allFocusableElements, "-1");
    subNav.setAttribute("aria-hidden", "true");
  };

  const initialiseMenu = () => {
    if (dom.$overlay) {
      dom.$overlay.setAttribute("aria-hidden", "true");
      dom.$overlay.inert = true;
      const focusableElements = dom.$overlay.querySelectorAll<HTMLElement>(
        "a, button, input, textarea, select, [tabindex]:not([tabindex=\"-1\"])",
      );
      setFocusableElements(focusableElements, "-1");
    }

    // Initialise subnav items as non-focusable and hidden from screen readers since they start hidden
    for (const subNav of dom.$subNavs) {
      disableSubnavFocusability(subNav);

      // Ensure accordion content is explicitly hidden from screen readers initially
      const accordionContentElements = subNav.querySelectorAll<HTMLElement>(".js-accordion-item-content");
      for (const contentElement of accordionContentElements) {
        contentElement.setAttribute("aria-hidden", "true");
      }
    }

    for (const navItem of dom.$navListItems) {
      if (navItem.tagName !== "BUTTON" && navItem.tagName !== "A") {
        continue;
      }

      // A nav item with no panel is a plain link, so it carries no submenu state
      if (findSubnavFor(navItem)) {
        navItem.setAttribute(ARIA_EXPANDED, "false");
      } else {
        navItem.removeAttribute(ARIA_EXPANDED);
      }
    }
  };

  const enableOverlay = () => {
    dom.$overlay?.removeAttribute("tabindex");
    dom.$overlay?.removeAttribute("aria-hidden");

    if (dom.$overlay) {
      dom.$overlay.inert = false;
    }

    const focusableElements = dom.$overlay?.querySelectorAll<HTMLElement>("[tabindex=\"-1\"]");
    if (focusableElements) {
      for (const el of focusableElements) {
        el.removeAttribute("tabindex");
      }
    }

    // Keep subnav items non-focusable initially since they start hidden
    for (const subNav of dom.$subNavs) {
      if (!subNav.classList.contains(CSS.active)) {
        disableSubnavFocusability(subNav);
      }
    }
  };

  const disableOverlay = () => {
    dom.$overlay?.setAttribute("tabindex", "-1");
    dom.$overlay?.setAttribute("aria-hidden", "true");

    // The panel is only moved out of view, so `inert` is what closes it off
    if (dom.$overlay) {
      dom.$overlay.inert = true;
    }

    const focusableElements = dom.$overlay?.querySelectorAll<HTMLElement>(
      "a, button, input, textarea, select, [tabindex]:not([tabindex=\"-1\"])",
    );
    if (focusableElements) {
      setFocusableElements(focusableElements, "-1");
    }
    dom.$openBtn?.focus();
  };

  const toggleOverlay = (isOpen: boolean) => {
    dom.$overlay?.classList.toggle(CSS.active, isOpen);

    if (isOpen) {
      enableOverlay();
    } else {
      disableOverlay();
    }
  };

  const handleBackButton = () => {
    // Clear navigation state
    stopAccordionWatcher();

    // Deactivate all subnavs
    for (const subNav of dom.$subNavs) {
      subNav.classList.remove(CSS.active);
      disableSubnavFocusability(subNav);
    }

    // Deactivate all nav items and reset their aria-expanded state
    for (const navItem of dom.$navListItems) {
      navItem.classList.remove(CSS.active);
      setExpandedState(navItem, false);
    }

    // Don't set focus - let it naturally move to avoid re-triggering subnav
  };

  const deactivateOtherSubnavs = (targetSubNav: HTMLElement) => {
    for (const otherSubNav of dom.$subNavs) {
      if (otherSubNav !== targetSubNav) {
        otherSubNav.classList.remove(CSS.active);
        disableSubnavFocusability(otherSubNav);
      }
    }
  };

  const deactivateOtherNavItems = (targetNavItem: HTMLElement) => {
    for (const otherNavItem of dom.$navListItems) {
      if (otherNavItem !== targetNavItem) {
        otherNavItem.classList.remove(CSS.active);
        setExpandedState(otherNavItem, false);
      }
    }
  };

  const initialiseSubnavScrolling = (subNav: HTMLElement) => {
    const accordion = subNav.querySelector<HTMLElement>(".js-accordion");

    if (accordion) {
      const subNavLenis = new Lenis({
        autoRaf: true,
        wrapper: subNav,
        content: accordion,
      });

      subNavLenis.start();
    }
  };

  const setActiveNav = (navItem: HTMLElement, subNav: HTMLElement) => {
    deactivateOtherSubnavs(subNav);
    deactivateOtherNavItems(navItem);

    navItem.classList.add(CSS.active);
    subNav.classList.add(CSS.active);

    setExpandedState(navItem, true);

    enableSubnavFocusability(subNav);
    initialiseSubnavScrolling(subNav);
  };

  // Tab stops in DOM order, skipping anything disabled or not displayed
  const getTabStops = (container: HTMLElement) => Array.from(container.querySelectorAll<HTMLElement>(
    "a[href], button, input, textarea, select, [tabindex]",
  )).filter($el => $el.getAttribute("tabindex") !== "-1" && $el.getClientRects().length > 0);

  const focusFirstSubnavElement = (correspondingSubnav: HTMLElement) => {
    getTabStops(correspondingSubnav)[0]?.focus();
  };

  const handleNavItemTabForward = (e: KeyboardEvent, navItem: HTMLElement, correspondingSubnav: HTMLElement) => {
    setActiveNav(navItem, correspondingSubnav);
    startAccordionWatcher();

    setTimeout(() => {
      focusFirstSubnavElement(correspondingSubnav);
    }, 50);

    e.preventDefault();
  };

  const findCorrespondingNavItem = (activeSubnav: HTMLElement) => {
    const subnavId = activeSubnav.getAttribute("subnav-id");
    return Array.from(dom.$navListItems).find(
      navItem => navItem.getAttribute("subnav-id") === subnavId,
    );
  };

  // Shift+Tab onto a nav item with a subnav enters the subnav at its last stop,
  // mirroring Tab, which enters at the first
  const handleMainNavShiftTab = (e: KeyboardEvent, currentElement: HTMLElement) => {
    if (!dom.$mainNav) {
      return;
    }

    const mainNavStops = getTabStops(dom.$mainNav);
    const $previous = mainNavStops[mainNavStops.indexOf(currentElement) - 1];
    const subNav = $previous ? findSubnavFor($previous) : null;

    if (!$previous || !subNav) {
      return;
    }

    setActiveNav($previous, subNav);

    const $last = getTabStops(subNav).at(-1);

    if ($last) {
      e.preventDefault();
      $last.focus();
    }
  };

  // Shift+Tab from the first stop returns to the nav item; Tab from the last
  // continues to whatever follows the nav item in the main nav
  const handleSubnavTabNavigation = (e: KeyboardEvent, activeSubnav: HTMLElement, currentElement: HTMLElement) => {
    const subnavStops = getTabStops(activeSubnav);
    const navItem = findCorrespondingNavItem(activeSubnav);

    if (!navItem || !dom.$mainNav) {
      return;
    }

    if (e.shiftKey && currentElement === subnavStops[0]) {
      e.preventDefault();
      stopAccordionWatcher();
      navItem.focus();
      return;
    }

    if (!e.shiftKey && currentElement === subnavStops.at(-1)) {
      const mainNavStops = getTabStops(dom.$mainNav);
      const $next = mainNavStops[mainNavStops.indexOf(navItem) + 1];

      if ($next) {
        e.preventDefault();
        stopAccordionWatcher();
        $next.focus();
      }
    }
  };

  if (dom.$mainNav) {
    const navLenis = new Lenis({
      autoRaf: true,
      wrapper: dom.$mainNav,
    });

    navLenis.start();
  }

  for (const navItem of dom.$navListItems) {
    const correspondingSubnav = Array.from(dom.$subNavs).find(
      subNav => subNav.getAttribute("subnav-id") === navItem.getAttribute("subnav-id"),
    );

    if (correspondingSubnav) {
      const activateNavItem = (e: Event) => {
        e.preventDefault();
        setActiveNav(navItem, correspondingSubnav);
      };

      const activateNavItemOnFocus = () => {
        setActiveNav(navItem, correspondingSubnav);
      };

      navItem.addEventListener("mouseenter", activateNavItem);
      navItem.addEventListener("click", activateNavItem);
      navItem.addEventListener("focus", activateNavItemOnFocus);

      navItem.addEventListener("keydown", (e) => {
        if (e.key === "Tab" && !e.shiftKey) {
          handleNavItemTabForward(e, navItem, correspondingSubnav);
        }
      });
    }
  }

  for (const backBtn of dom.$backBtns) {
    backBtn.addEventListener("click", handleBackButton);
  }

  dom.$openBtn?.addEventListener("click", () => {
    toggleOverlay(true);
    const firstFocusable = dom.$overlay?.querySelector<HTMLElement>(
      "a:not([tabindex=\"-1\"]), button:not([tabindex=\"-1\"])",
    );
    firstFocusable?.focus();
  });

  dom.$closeBtn?.addEventListener("click", () => {
    toggleOverlay(false);
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && dom.$overlay?.classList.contains(CSS.active)) {
      toggleOverlay(false);
      return;
    }

    if (e.key === "Tab" && dom.$overlay?.classList.contains(CSS.active)) {
      const currentElement = document.activeElement as HTMLElement;
      const activeSubnav = Array.from(dom.$subNavs).find(subNav => subNav.classList.contains(CSS.active));

      if (activeSubnav?.contains(currentElement)) {
        handleSubnavTabNavigation(e, activeSubnav, currentElement);
      } else if (e.shiftKey && dom.$mainNav?.contains(currentElement)) {
        handleMainNavShiftTab(e, currentElement);
      }
    }
  });

  initialiseMenu();
};

export default () => {
  const $nav = document.querySelectorAll<HTMLElement>(".js-nav");

  for (const $element of $nav) {
    instance($element);
  }
};
